import { useTranslation } from 'react-i18next'

import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { isWorkflowCaseReadonly } from '~/models/workflowCaseReadonly'

type WorkflowLockReasons = {
  /**
   * 日付操作 UI を非活性にしている間の押せない理由 (spec AC-WFW-006a)。
   * workflowId 付きで開いている間 (`isDateLocked`) だけ値を持つ。
   */
  dateLockedReason: string | undefined
  /**
   * 読み取り専用で非活性にした編集・確定操作の押せない理由 (spec AC-WFW-014)。
   * ワークフローを取得済みで完了済み・削除済みの間だけ値を持つ。`isReadonly` は取得中・
   * 取得失敗でも true (fail-closed) だが、その間は「完了済み・削除済み」 と言える事実が
   * 無いので理由は出さない。
   */
  readonlyReason: string | undefined
}

/**
 * 業務ワークフローの遷移先画面で、開いた入口のために押せない操作の理由
 * (conventions.md §操作できない時のボタンの扱いの 3。ツールチップで示す) を返す。
 *
 * 文言と出す条件を本 hook に集め、画面ごとに条件式と文言 key を写さない。画面固有の
 * 押せない理由と同時に当たる時は `readonlyReason` を優先して単独で出す (開いた入口の
 * 不可は画面内の操作で解消しないため)。
 */
export function useWorkflowLockReasons(): WorkflowLockReasons {
  const { t } = useTranslation()
  const { wfCase, isDateLocked } = useCurrentWorkflowCase()
  return {
    dateLockedReason: isDateLocked
      ? t('common:workflowLock.dateLockedReason')
      : undefined,
    readonlyReason:
      wfCase !== undefined && isWorkflowCaseReadonly(wfCase)
        ? t('common:workflowLock.readonlyReason')
        : undefined,
  }
}
